import { useState, useEffect, useCallback, useRef } from "react";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Separator } from "../components/ui/separator";
import { MessageCircle, Send, ArrowLeft, Phone, User, RefreshCw, Inbox } from "lucide-react";
import { toast } from "sonner";
import { format, parseISO } from "date-fns";

export default function WhatsAppPage() {
  const [conversations, setConversations] = useState([]);
  const [selectedPhone, setSelectedPhone] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [newPhone, setNewPhone] = useState("");
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [showNewChat, setShowNewChat] = useState(false);
  const messagesEndRef = useRef(null);

  const fetchConversations = useCallback(async () => {
    try {
      const [convRes, statsRes] = await Promise.all([
        api.get("/whatsapp/conversations"),
        api.get("/whatsapp/stats")
      ]);
      setConversations(convRes.data);
      setStats(statsRes.data);
    } catch (err) { console.error("Failed to load WhatsApp conversations", err); }
    setLoading(false);
  }, []);

  useEffect(() => { fetchConversations(); }, [fetchConversations]);

  // Auto-refresh every 10s
  useEffect(() => {
    const interval = setInterval(fetchConversations, 10000);
    return () => clearInterval(interval);
  }, [fetchConversations]);

  const selectConversation = async (phone) => {
    setSelectedPhone(phone);
    setShowNewChat(false);
    try {
      const res = await api.get("/whatsapp/messages", { params: { phone } });
      setMessages(res.data.reverse());
      // Mark as read
      await api.patch(`/whatsapp/messages/${encodeURIComponent(phone)}/read`);
      fetchConversations();
    } catch { toast.error("Failed to load messages"); }
  };

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  // Auto refresh messages for selected conversation
  useEffect(() => {
    if (!selectedPhone) return;
    const interval = setInterval(async () => {
      try {
        const res = await api.get("/whatsapp/messages", { params: { phone: selectedPhone } });
        setMessages(res.data.reverse());
      } catch (err) { console.warn("WhatsApp message poll failed", err); }
    }, 8000);
    return () => clearInterval(interval);
  }, [selectedPhone]);

  const handleSend = async () => {
    const phone = showNewChat ? newPhone : selectedPhone;
    if (!phone || !newMessage.trim()) return;
    setSending(true);
    try {
      await api.post("/whatsapp/send", { to_phone: phone, message: newMessage.trim() });
      setNewMessage("");
      if (showNewChat) {
        setShowNewChat(false);
        setSelectedPhone(phone);
      }
      // Refresh messages
      const res = await api.get("/whatsapp/messages", { params: { phone } });
      setMessages(res.data.reverse());
      fetchConversations();
      toast.success("Message sent");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to send");
    }
    setSending(false);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <div data-testid="whatsapp-page" className="space-y-6">
      <div className="flex items-center justify-between animate-fade-in-up">
        <div>
          <h1 className="font-serif text-3xl font-semibold text-foreground">WhatsApp Inbox</h1>
          <p className="text-muted-foreground text-sm mt-1">Send and receive WhatsApp messages</p>
        </div>
        <div className="flex items-center gap-2">
          {stats && (
            <div className="flex items-center gap-3 text-xs text-muted-foreground mr-2">
              <span>Total: {stats.total}</span>
              <span className="text-green-400">In: {stats.incoming}</span>
              <span className="gold-text">Out: {stats.outgoing}</span>
              {stats.unread > 0 && (
                <Badge className="bg-red-500/20 text-red-400 border-red-500/30 text-[10px]">{stats.unread} unread</Badge>
              )}
            </div>
          )}
          <Button variant="outline" size="sm" onClick={fetchConversations} className="border-border rounded-sm gap-1">
            <RefreshCw className="w-3.5 h-3.5" /> Refresh
          </Button>
        </div>
      </div>

      {/* Twilio Info + Webhook Setup */}
      <div className="bg-[rgba(34,197,94,0.06)] border border-[rgba(34,197,94,0.15)] rounded-sm p-4 space-y-3">
        <div className="flex items-center gap-3">
          <MessageCircle className="w-4 h-4 text-green-400 shrink-0" />
          <p className="text-xs text-muted-foreground">
            <span className="text-green-400 font-medium">Twilio WhatsApp Connected</span> — Sending via Sandbox (+14155238886)
          </p>
        </div>
        <details className="text-xs">
          <summary className="text-muted-foreground cursor-pointer hover:text-foreground transition-colors font-medium">
            Setup incoming messages (webhook configuration)
          </summary>
          <div className="mt-2 pl-7 space-y-1.5 text-muted-foreground">
            <p>1. Go to <a href="https://console.twilio.com" target="_blank" rel="noopener noreferrer" className="gold-text underline">console.twilio.com</a></p>
            <p>2. Navigate to <span className="text-foreground">Messaging → Try it Out → Send a WhatsApp message</span></p>
            <p>3. Join the sandbox by sending the join code from your personal WhatsApp</p>
            <p>4. Go to <span className="text-foreground">Messaging → Settings → WhatsApp Sandbox Settings</span></p>
            <p>5. Set <span className="text-foreground">"When a message comes in"</span> to:</p>
            <div className="bg-background rounded-sm px-3 py-1.5 border border-border font-mono text-foreground select-all mt-1">
              {window.location.origin}/api/whatsapp/webhook
            </div>
            <p>6. Set method to <span className="text-foreground">POST</span> and Save</p>
            <p className="text-muted-foreground/60 mt-2">After setup, any WhatsApp message sent to the sandbox number will appear here automatically.</p>
          </div>
        </details>
      </div>

      <div className="grid grid-cols-[320px_1fr] gap-4 h-[calc(100vh-260px)]">
        {/* Conversations List */}
        <Card className="luxury-card rounded-sm flex flex-col overflow-hidden">
          <div className="p-3 border-b border-border flex items-center justify-between">
            <span className="text-xs uppercase tracking-wider text-muted-foreground font-medium">Conversations</span>
            <Button
              data-testid="new-chat-btn"
              variant="ghost"
              size="sm"
              onClick={() => { setShowNewChat(true); setSelectedPhone(null); }}
              className="h-7 text-xs gold-text hover:text-[#D4AF37]/80"
            >
              + New
            </Button>
          </div>
          <div className="flex-1 overflow-y-auto">
            {loading ? (
              [...Array(3)].map((_, i) => <div key={i} className="h-16 m-2 bg-muted/20 rounded animate-pulse" />)
            ) : conversations.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full text-muted-foreground p-4">
                <Inbox className="w-8 h-8 mb-2 opacity-30" />
                <p className="text-sm">No conversations yet</p>
                <p className="text-xs mt-1">Send a message to start</p>
              </div>
            ) : conversations.map((conv) => (
              <button
                key={conv.phone}
                data-testid={`conversation-${conv.phone}`}
                onClick={() => selectConversation(conv.phone)}
                className={`w-full text-left px-3 py-3 border-b border-border transition-colors ${
                  selectedPhone === conv.phone ? "bg-[rgba(114,47,55,0.15)]" : "hover:bg-white/[0.03]"
                }`}
              >
                <div className="flex items-center justify-between mb-1">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-full bg-green-500/10 flex items-center justify-center shrink-0">
                      {conv.customer_name ? (
                        <User className="w-3.5 h-3.5 text-green-400" />
                      ) : (
                        <Phone className="w-3.5 h-3.5 text-green-400" />
                      )}
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-foreground truncate">
                        {conv.customer_name || conv.phone}
                      </p>
                      {conv.customer_name && <p className="text-[10px] text-muted-foreground">{conv.phone}</p>}
                    </div>
                  </div>
                  {conv.unread_count > 0 && (
                    <Badge className="bg-green-500 text-white text-[10px] h-5 min-w-[20px] flex items-center justify-center rounded-full">
                      {conv.unread_count}
                    </Badge>
                  )}
                </div>
                <p className="text-xs text-muted-foreground truncate pl-10">
                  {conv.last_direction === "outgoing" && <span className="gold-text">You: </span>}
                  {conv.last_message}
                </p>
                <p className="text-[10px] text-muted-foreground/60 pl-10 mt-0.5">
                  {conv.last_time ? format(parseISO(conv.last_time), "dd MMM, hh:mm a") : ""}
                </p>
              </button>
            ))}
          </div>
        </Card>

        {/* Chat Area */}
        <Card className="luxury-card rounded-sm flex flex-col overflow-hidden">
          {showNewChat ? (
            /* New Chat */
            <>
              <div className="p-3 border-b border-border flex items-center gap-3">
                <Button variant="ghost" size="icon" onClick={() => setShowNewChat(false)} className="h-8 w-8">
                  <ArrowLeft className="w-4 h-4" />
                </Button>
                <span className="text-sm font-medium text-foreground">New Message</span>
              </div>
              <div className="flex-1 flex flex-col items-center justify-center p-6">
                <MessageCircle className="w-12 h-12 text-green-400/30 mb-4" />
                <p className="text-sm text-muted-foreground mb-4">Enter phone number to send a WhatsApp message</p>
                <div className="w-full max-w-sm space-y-3">
                  <Input
                    data-testid="new-chat-phone"
                    value={newPhone}
                    onChange={(e) => setNewPhone(e.target.value)}
                    placeholder="+91 9876543210"
                    className="bg-background border-border rounded-sm"
                  />
                  <textarea
                    data-testid="new-chat-message"
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    onKeyDown={handleKeyDown}
                    placeholder="Type your message..."
                    className="w-full h-24 bg-background border border-border rounded-sm px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground resize-none outline-none focus:border-[rgba(212,175,55,0.5)]"
                  />
                  <Button
                    data-testid="send-new-message-btn"
                    onClick={handleSend}
                    disabled={sending || !newPhone || !newMessage.trim()}
                    className="w-full btn-maroon rounded-sm gap-2"
                  >
                    <Send className="w-4 h-4" /> {sending ? "Sending..." : "Send Message"}
                  </Button>
                </div>
              </div>
            </>
          ) : selectedPhone ? (
            /* Conversation View */
            <>
              <div className="p-3 border-b border-border flex items-center gap-3">
                <div className="w-8 h-8 rounded-full bg-green-500/10 flex items-center justify-center">
                  <MessageCircle className="w-4 h-4 text-green-400" />
                </div>
                <div>
                  <p className="text-sm font-medium text-foreground">
                    {conversations.find(c => c.phone === selectedPhone)?.customer_name || selectedPhone}
                  </p>
                  <p className="text-[10px] text-muted-foreground">{selectedPhone}</p>
                </div>
              </div>

              {/* Messages */}
              <div className="flex-1 overflow-y-auto p-4 space-y-3">
                {messages.map((msg) => (
                  <div
                    key={msg.id}
                    data-testid={`message-${msg.id}`}
                    className={`flex ${msg.direction === "outgoing" ? "justify-end" : "justify-start"}`}
                  >
                    <div
                      className={`max-w-[70%] rounded-sm px-3 py-2 ${
                        msg.direction === "outgoing"
                          ? "bg-[rgba(114,47,55,0.3)] border border-[rgba(114,47,55,0.4)]"
                          : "bg-[rgba(34,197,94,0.08)] border border-[rgba(34,197,94,0.15)]"
                      }`}
                    >
                      <p className="text-sm text-foreground whitespace-pre-wrap">{msg.message}</p>
                      <div className="flex items-center gap-2 mt-1">
                        <p className="text-[10px] text-muted-foreground">
                          {msg.created_at ? format(parseISO(msg.created_at), "hh:mm a") : ""}
                        </p>
                        {msg.direction === "outgoing" && (
                          <span className={`text-[9px] ${msg.status === "MOCKED" ? "text-amber-400" : msg.status?.includes("FAILED") ? "text-red-400" : "text-green-400"}`}>
                            {msg.status === "queued" || msg.status === "sent" ? "Sent" : msg.status}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
                <div ref={messagesEndRef} />
              </div>

              {/* Input */}
              <div className="p-3 border-t border-border flex items-center gap-2">
                <textarea
                  data-testid="chat-message-input"
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder="Type a message..."
                  rows={1}
                  className="flex-1 bg-background border border-border rounded-sm px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground resize-none outline-none focus:border-[rgba(212,175,55,0.5)] max-h-20"
                />
                <Button
                  data-testid="send-message-btn"
                  onClick={handleSend}
                  disabled={sending || !newMessage.trim()}
                  className="btn-maroon rounded-sm h-9 w-9 p-0"
                >
                  <Send className="w-4 h-4" />
                </Button>
              </div>
            </>
          ) : (
            /* Empty State */
            <div className="flex-1 flex flex-col items-center justify-center text-muted-foreground">
              <MessageCircle className="w-16 h-16 mb-4 opacity-10" />
              <p className="text-sm">Select a conversation or start a new one</p>
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}
